import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { CloseIcon, DeleteIcon, EditIcon, SearchIcon } from "@chakra-ui/icons";
import { createColumnHelper } from "@tanstack/react-table";
import { useCallback, useMemo, useRef, useState } from "react";
import WelcomeSection from "components/WelcomeSection";
import { ReactTable } from "components/ReactTable";
import {
  useTrackingAddresses,
  type TrackingAddress,
  type UpsertAddressPayload,
} from "hooks/useTrackingAddresses";
import { showToast, toastTypes } from "utils";

type FormMode = "create" | "edit";

function formatDate(s: string | null | undefined) {
  if (!s) return "—";
  return new Date(s).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export default function TrackingAddressesPage() {
  const toast = useToast();
  const {
    addresses,
    isLoading,
    error,
    createAddress,
    updateAddress,
    setDefaultAddress,
    removeAddress,
  } = useTrackingAddresses();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isConfirmOpen,
    onOpen: onConfirmOpen,
    onClose: onConfirmClose,
  } = useDisclosure();
  const cancelRef = useRef<any>(null);

  const [formMode, setFormMode] = useState<FormMode>("create");
  const [editingAddress, setEditingAddress] = useState<TrackingAddress | null>(null);
  const [formValues, setFormValues] = useState<UpsertAddressPayload>({
    label: "",
    map_address: "",
    latitude: undefined,
    longitude: undefined,
    is_default: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [settingDefaultId, setSettingDefaultId] = useState<number | null>(null);
  const [activeItem, setActiveItem] = useState<TrackingAddress | null>(null);
  const [searchValue, setSearchValue] = useState("");

  const resetForm = useCallback(() => {
    setFormValues({
      label: "",
      map_address: "",
      latitude: undefined,
      longitude: undefined,
      is_default: false,
    });
    setEditingAddress(null);
    setFormMode("create");
  }, []);

  const openCreateModal = useCallback(() => {
    resetForm();
    setFormMode("create");
    onOpen();
  }, [onOpen, resetForm]);

  const openEditModal = useCallback(
    (address: TrackingAddress) => {
      setEditingAddress(address);
      setFormMode("edit");
      setFormValues({
        label: address.label,
        map_address: address.map_address ?? "",
        latitude: address.latitude ?? undefined,
        longitude: address.longitude ?? undefined,
        is_default: address.is_default,
      });
      onOpen();
    },
    [onOpen]
  );

  const handleChange =
    (field: keyof UpsertAddressPayload) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      if (field === "latitude" || field === "longitude") {
        setFormValues((prev) => ({
          ...prev,
          [field]: value === "" ? undefined : Number(value),
        }));
      } else if (field === "is_default") {
        setFormValues((prev) => ({
          ...prev,
          is_default: event.target.checked,
        }));
      } else {
        setFormValues((prev) => ({
          ...prev,
          [field]: value,
        }));
      }
    };

  const handleSubmit = useCallback(async () => {
    if (!formValues.label.trim()) {
      showToast({
        toast,
        message: "Le libellé est obligatoire.",
        status: toastTypes.error,
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const payload: UpsertAddressPayload = {
        label: formValues.label.trim(),
        map_address: formValues.map_address?.toString().trim() || "",
        latitude:
          formValues.latitude === undefined || Number.isNaN(formValues.latitude)
            ? null
            : formValues.latitude,
        longitude:
          formValues.longitude === undefined || Number.isNaN(formValues.longitude)
            ? null
            : formValues.longitude,
        is_default: formValues.is_default,
      };
      if (formMode === "create") {
        await createAddress(payload);
        showToast({ toast, message: "Adresse créée avec succès.", status: toastTypes.success });
      } else if (editingAddress) {
        await updateAddress(editingAddress.id, payload);
        showToast({ toast, message: "Adresse mise à jour avec succès.", status: toastTypes.success });
      }
      onClose();
      resetForm();
    } catch (e: any) {
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.detail ||
        e?.message ||
        "Erreur lors de l'enregistrement de l'adresse.";
      showToast({ toast, message: msg, status: toastTypes.error });
    } finally {
      setIsSubmitting(false);
    }
  }, [createAddress, updateAddress, editingAddress, formMode, formValues, onClose, resetForm, toast]);

  const handleDeleteClick = useCallback((address: TrackingAddress) => {
    setActiveItem(address);
    onConfirmOpen();
  }, [onConfirmOpen]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!activeItem) return;
    setDeletingId(activeItem.id);
    try {
      await removeAddress(activeItem.id);
      showToast({ toast, message: "Adresse supprimée avec succès.", status: toastTypes.success });
      onConfirmClose();
      setActiveItem(null);
    } catch (e: any) {
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.detail ||
        e?.message ||
        "Erreur lors de la suppression.";
      showToast({ toast, message: msg, status: toastTypes.error });
    } finally {
      setDeletingId(null);
    }
  }, [activeItem, removeAddress, toast, onConfirmClose]);

  const handleSetDefault = useCallback(
    async (address: TrackingAddress) => {
      setSettingDefaultId(address.id);
      try {
        await setDefaultAddress(address.id);
        showToast({ toast, message: "Adresse définie comme par défaut.", status: toastTypes.success });
      } catch (e: any) {
        const msg =
          e?.response?.data?.error ||
          e?.response?.data?.detail ||
          e?.message ||
          "Erreur lors de la mise à jour.";
        showToast({ toast, message: msg, status: toastTypes.error });
      } finally {
        setSettingDefaultId(null);
      }
    },
    [setDefaultAddress, toast]
  );

  const sortedAddresses = useMemo(() => {
    const list = Array.isArray(addresses) ? addresses : [];
    return [...list].sort((a, b) => {
      if (a.is_default && !b.is_default) return -1;
      if (!a.is_default && b.is_default) return 1;
      return a.label.localeCompare(b.label);
    });
  }, [addresses]);

  const filteredData = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    if (!q) return sortedAddresses;
    return sortedAddresses.filter(
      (a) =>
        a.label.toLowerCase().includes(q) ||
        (a.map_address && a.map_address.toLowerCase().includes(q))
    );
  }, [sortedAddresses, searchValue]);

  const tableColumns = useMemo(() => {
    const col = createColumnHelper<TrackingAddress>();
    return [
      col.accessor("label", {
        cell: (info) => (
          <Flex gap="8px" alignItems="center" px="12px" py="0px">
            <Text>{info.getValue()}</Text>
            {info.row.original.is_assigned && !info.row.original.is_owned && (
              <Badge colorScheme="purple" fontSize="xs">
                Assignée
              </Badge>
            )}
          </Flex>
        ),
        header: () => (
          <Flex p="25px">
            <Heading as="h6">Libellé</Heading>
          </Flex>
        ),
        meta: { width: "180px" },
      }),
      col.accessor("map_address", {
        cell: (info) => (
          <Flex px="12px" py="0px" maxW="200px">
            <Text noOfLines={2} fontSize="sm">
              {info.getValue() || "—"}
            </Text>
          </Flex>
        ),
        header: () => (
          <Flex px="25px" py="14px">
            <Heading as="h6">Adresse</Heading>
          </Flex>
        ),
        meta: { width: "200px" },
      }),
      col.accessor(
        (row) =>
          [row.latitude, row.longitude].some((v) => v != null)
            ? `${row.latitude ?? "—"} / ${row.longitude ?? "—"}`
            : "—",
        {
          id: "coords",
          cell: (info) => (
            <Flex px="12px" py="0px">
              <Text fontSize="sm">{info.getValue()}</Text>
            </Flex>
          ),
          header: () => (
            <Flex px="25px" py="14px">
              <Heading as="h6">Coordonnées</Heading>
            </Flex>
          ),
          meta: { width: "140px" },
        }
      ),
      col.accessor("is_default", {
        cell: (info) => (
          <Flex justifyContent="flex-start" width="100px">
            {info.getValue() ? (
              <Badge colorScheme="green" size="sm" borderRadius="4px" px="8px" py="2px">
                Par défaut
              </Badge>
            ) : (
              <Text fontSize="sm" color="gray.500">
                —
              </Text>
            )}
          </Flex>
        ),
        header: () => (
          <Flex px="15px" py="14px">
            <Heading as="h6">Par défaut</Heading>
          </Flex>
        ),
        meta: { width: "100px" },
      }),
      col.accessor("created_at", {
        cell: (info) => (
          <Flex justifyContent="flex-end" px="12px" py="0px">
            <Text>{formatDate(info.getValue())}</Text>
          </Flex>
        ),
        header: () => (
          <Flex px="25px" py="14px" justifyContent="flex-end">
            <Heading as="h6">Date de création</Heading>
          </Flex>
        ),
        meta: { width: "120px" },
      }),
      col.accessor("id", {
        id: "action",
        cell: (info) => {
          const row = info.row.original;
          return (
            <HStack gap="2px">
              <IconButton
                variant="outline"
                colorScheme="gray_stroke_0"
                aria-label="Modifier"
                size="sm"
                icon={<EditIcon />}
                borderRadius="4px"
                onClick={() => openEditModal(row)}
                isDisabled={row.is_assigned && !row.is_owned}
              />
              {!row.is_default && row.is_owned && (
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="gray_stroke_0"
                  borderRadius="4px"
                  onClick={() => handleSetDefault(row)}
                  isLoading={settingDefaultId === row.id}
                >
                  Défaut
                </Button>
              )}
              <IconButton
                variant="outline"
                colorScheme="red_origin"
                aria-label="Supprimer"
                size="sm"
                icon={<DeleteIcon />}
                borderRadius="4px"
                onClick={() => handleDeleteClick(row)}
                isDisabled={!row.is_owned}
                isLoading={deletingId === row.id}
              />
            </HStack>
          );
        },
        header: () => (
          <Flex px="15px" py="14px">
            <Heading as="h6">Action</Heading>
          </Flex>
        ),
        meta: { width: "140px" },
      }),
    ];
  }, [openEditModal, handleSetDefault, handleDeleteClick, settingDefaultId, deletingId]);

  return (
    <>
      <Flex
        gap="18px"
        bg="very_light_gray"
        flex={1}
        flexDirection="column"
        py="20px"
      >
        <WelcomeSection />

        <Flex
          flexDirection="column"
          alignItems="start"
          px={{ base: "20px", sm: "24px" }}
          minHeight="100vh"
        >
          <Flex gap="4px" alignSelf="stretch" flexDirection="column">
            <Flex
              justifyContent="center"
              alignItems="center"
              flexDirection={{ base: "column", sm: "row" }}
            >
              <Heading size="text2xl" as="h5" fontFamily="Arimo">
                Adresses
              </Heading>
              <Flex
                pl="56px"
                pr="130px"
                gap="13px"
                flex={1}
                justifyContent="flex-end"
                alignItems="center"
                alignSelf={{ base: "stretch", sm: "auto" }}
                flexDirection={{ base: "column", sm: "row" }}
                px={{ md: 0, base: "20px" }}
              >
                <Button
                  size="xl"
                  variant="outline"
                  colorScheme="red_origin"
                  fontWeight={700}
                  gap="8px"
                  minW="160px"
                  borderRadius="8px"
                  onClick={openCreateModal}
                >
                  Créer une adresse
                </Button>
              </Flex>
            </Flex>
          </Flex>

          {error && (
            <Box mt={4} p={4} bg="red.50" borderRadius="md">
              <Text color="red.700">
                Erreur lors du chargement des adresses. Vérifiez que l&apos;API Tracking est disponible.
              </Text>
            </Box>
          )}

          <Box mt="16px" bg="white" w="100%" borderRadius="10px">
            <Flex
              bg="white"
              justifyContent="center"
              alignItems="center"
              px="12px"
              py="16px"
              flexDirection={{ md: "row", base: "column" }}
            >
              <Flex
                flex={1}
                alignItems="center"
                alignSelf={{ md: "auto", base: "stretch" }}
                flexDirection={{ base: "column", sm: "row" }}
              >
                <InputGroup w={{ base: "100%", sm: "30%" }}>
                  <Input
                    placeholder="Rechercher une adresse"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    gap="8px"
                    borderWidth="1px"
                    borderRadius="8px"
                  />
                  <InputRightElement>
                    {searchValue.length > 0 ? (
                      <IconButton
                        aria-label="Effacer"
                        size="sm"
                        variant="ghost"
                        icon={<CloseIcon />}
                        onClick={() => setSearchValue("")}
                      />
                    ) : null}
                  </InputRightElement>
                </InputGroup>
                <IconButton
                  ml={2}
                  borderRadius="10px"
                  py="19px"
                  border="1px solid #e2e4e6"
                  aria-label="Rechercher"
                  icon={<SearchIcon />}
                  justifyContent="center"
                  alignItems="center"
                />
              </Flex>
            </Flex>

            {isLoading && sortedAddresses.length === 0 ? (
              <Flex justify="center" py={10}>
                <Spinner size="lg" />
              </Flex>
            ) : (
              <ReactTable
                size="sm"
                headerCellProps={{ bg: "white" }}
                tableProps={{
                  borderColor: "neutral.600",
                  borderTopWidth: "1px",
                  borderStyle: "solid",
                }}
                columns={tableColumns as any}
                data={filteredData}
              />
            )}
            {!isLoading && filteredData.length === 0 && (
              <Box p={8} textAlign="center">
                <Text color="gray.600">
                  {searchValue.trim() ? "Aucun résultat pour cette recherche." : "Aucune adresse définie."}
                </Text>
              </Box>
            )}
          </Box>
        </Flex>
      </Flex>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {formMode === "create" ? "Ajouter une adresse" : "Modifier l'adresse"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <FormControl isRequired>
                <FormLabel>Libellé</FormLabel>
                <Input
                  value={formValues.label}
                  onChange={handleChange("label")}
                  placeholder="Ex. Maison, École, Bureau"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Adresse (texte)</FormLabel>
                <Input
                  value={formValues.map_address ?? ""}
                  onChange={handleChange("map_address")}
                  placeholder="Adresse détaillée ou repère"
                />
              </FormControl>
              <HStack>
                <FormControl>
                  <FormLabel>Latitude</FormLabel>
                  <Input
                    type="number"
                    step="0.000001"
                    value={formValues.latitude ?? ""}
                    onChange={handleChange("latitude")}
                    placeholder="Ex. 5.345678"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Longitude</FormLabel>
                  <Input
                    type="number"
                    step="0.000001"
                    value={formValues.longitude ?? ""}
                    onChange={handleChange("longitude")}
                    placeholder="-3.987654"
                  />
                </FormControl>
              </HStack>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Annuler
            </Button>
            <Button colorScheme="blue" onClick={handleSubmit} isLoading={isSubmitting}>
              Enregistrer
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog
        isOpen={isConfirmOpen}
        leastDestructiveRef={cancelRef}
        onClose={onConfirmClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Supprimer l&apos;adresse</AlertDialogHeader>
            <AlertDialogBody>
              Êtes-vous sûr de vouloir supprimer cette adresse ?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onConfirmClose}>
                Annuler
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDeleteConfirm}
                isLoading={deletingId === activeItem?.id}
                ml={3}
              >
                Supprimer
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
